import React, { useState, useEffect } from 'react';
import api from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { Plus, Wallet, TrendingUp, TrendingDown } from 'lucide-react';

const CajaChica = () => {
  const [data, setData] = useState({ movimientos: [], balance: 0, total_ingresos: 0, total_egresos: 0 });
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [formData, setFormData] = useState({ descripcion: '', monto: 0, tipo: 'egreso', fecha: new Date().toISOString().split('T')[0], categoria: '', metodo_pago: 'efectivo' });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const response = await api.get('/expenses/caja-chica');
      setData(response.data);
    } catch (error) {
      toast.error('Error al cargar caja chica');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await api.post('/expenses/caja-chica', formData);
      toast.success('Movimiento registrado');
      fetchData();
      setDialogOpen(false);
      setFormData({ descripcion: '', monto: 0, tipo: 'egreso', fecha: new Date().toISOString().split('T')[0], categoria: '', metodo_pago: 'efectivo' });
    } catch (error) {
      toast.error('Error');
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 p-6 pb-24">
      <div className="max-w-6xl mx-auto">
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="text-2xl flex items-center"><Wallet className="mr-2" />Caja Chica</CardTitle>
              <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                <DialogTrigger asChild><Button><Plus className="mr-2 h-4 w-4" />Nuevo Movimiento</Button></DialogTrigger>
                <DialogContent>
                  <DialogHeader><DialogTitle>Registrar Movimiento</DialogTitle></DialogHeader>
                  <form onSubmit={handleSubmit} className="space-y-4">
                    <div><Label>Tipo *</Label>
                      <Select value={formData.tipo} onValueChange={(value) => setFormData({...formData, tipo: value})}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent><SelectItem value="ingreso">Ingreso</SelectItem><SelectItem value="egreso">Egreso</SelectItem></SelectContent>
                      </Select>
                    </div>
                    <div><Label>Descripción *</Label><Input value={formData.descripcion} onChange={(e) => setFormData({...formData, descripcion: e.target.value})} required /></div>
                    <div><Label>Monto *</Label><Input type="number" step="0.01" value={formData.monto} onChange={(e) => setFormData({...formData, monto: parseFloat(e.target.value) || 0})} required /></div>
                    <div><Label>Fecha</Label><Input type="date" value={formData.fecha} onChange={(e) => setFormData({...formData, fecha: e.target.value})} /></div>
                    <div className="flex justify-end space-x-2">
                      <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Cancelar</Button>
                      <Button type="submit">Registrar</Button>
                    </div>
                  </form>
                </DialogContent>
              </Dialog>
            </div>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-3 gap-4 mb-6">
              <div className="p-4 bg-green-50 rounded-lg">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600">Ingresos</p>
                    <p className="text-2xl font-bold text-green-600">${data.total_ingresos?.toFixed(2)}</p>
                  </div>
                  <TrendingUp className="h-8 w-8 text-green-500" />
                </div>
              </div>
              <div className="p-4 bg-red-50 rounded-lg">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600">Egresos</p>
                    <p className="text-2xl font-bold text-red-600">${data.total_egresos?.toFixed(2)}</p>
                  </div>
                  <TrendingDown className="h-8 w-8 text-red-500" />
                </div>
              </div>
              <div className="p-4 bg-blue-50 rounded-lg border-2 border-blue-300">
                <div>
                  <p className="text-sm text-gray-600">Balance</p>
                  <p className="text-2xl font-bold text-blue-600">${data.balance?.toFixed(2)}</p>
                </div>
              </div>
            </div>
            {loading ? <div className="text-center py-8">Cargando...</div> : (
              <Table>
                <TableHeader><TableRow><TableHead>Fecha</TableHead><TableHead>Tipo</TableHead><TableHead>Descripción</TableHead><TableHead className="text-right">Monto</TableHead></TableRow></TableHeader>
                <TableBody>
                  {data.movimientos.length === 0 ? <TableRow><TableCell colSpan={4} className="text-center">No hay movimientos</TableCell></TableRow> : data.movimientos.map((mov) => (
                    <TableRow key={mov.id}>
                      <TableCell>{mov.fecha}</TableCell>
                      <TableCell>{mov.tipo === 'ingreso' ? <span className="text-green-600 font-medium">Ingreso</span> : <span className="text-red-600 font-medium">Egreso</span>}</TableCell>
                      <TableCell>{mov.descripcion}</TableCell>
                      <TableCell className={`text-right font-semibold ${mov.tipo === 'ingreso' ? 'text-green-600' : 'text-red-600'}`}>{mov.tipo === 'ingreso' ? '+' : '-'}${mov.monto?.toFixed(2)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default CajaChica;
